/**
 * useSupplyComposer (Phase 92, UI-SPEC 1.2): the client's draft for building
 * one product on a 3PL's garment, the read-only compose preview, and the save.
 *
 * The preview is `productSync.previewSupplyCompose`, a READ-ONLY MUTATION
 * (a 250-variant input does not fit a GET URL, review P4). A mutation keeps no
 * cache of its own, so this hook holds the last successful result:
 *   - sent after a 300 ms debounce on each draft change, only once a garment
 *     is chosen; three quick changes inside the debounce send one call;
 *   - the last result stays on screen while the next one runs;
 *   - a request counter drops any response older than the latest request;
 *   - EVERY failure, TOO_MANY_REQUESTS included, sets one `previewError`
 *     (review P9); the next success clears it; `retryPreview` re-sends now.
 *
 * What is sent (92-22): a match the client made (`blankValue` null = leave
 * those variants out); a store value with no entry is auto-matched by the
 * server. Design choices are the client's own choice, else the 3PL's
 * suggestion from the last result (a colour with no entry prints light, so
 * the pre-filled dark ones must be sent). Only dark or explicitly chosen
 * colours are sent, so a garment with no suggestions costs one call.
 *
 * The save (92-27): `productSync.saveSupplyElection`, once per click. A click
 * while one is in flight is ignored (the caller keeps the button enabled, UI
 * 31). The server re-reads the product, re-composes and re-probes, so the
 * draft only carries the client's choices plus which print sets carry a back
 * (`backSets`, `singleSet`, from the staged designs). Every refusal comes back
 * as data (BLU-1277); a thrown error is mapped by its cause code first, a
 * `CODE:detail` message marker second, and the generic save refusal last. A
 * refusal shows as one sticky destructive toast (one id per product, so a
 * retry never stacks a second) plus the inline line; the draft stays. A retry
 * the server answers `changed: false` is success (the save is idempotent).
 * Saving never writes to Shopify; only the existing Sync selected run does.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';
import { isUncertainOutcomeError, readCauseCode } from '@/lib/trpc-error-utils';
import type { SharedGarmentOption } from '../components/shared-stock/SharedSkuPicker';
import { GARMENT_GROUPS_MAX, moveValuesToGarment } from '../lib/garment-groups';
import { readySourceRef } from '../lib/scope';
import type { PreviewProduct } from '../lib/types';
import type { ProductStaging } from './useStagedDesigns';

export type SupplyComposePreview = RouterOutputs['productSync']['previewSupplyCompose'];
export type SupplyComposePreviewOk = Extract<SupplyComposePreview, { ok: true }>;
export type SupplyPreviewMatchRow = SupplyComposePreviewOk['matches']['sizes'][number];
export type SupplyPreviewColourMatchRow = SupplyComposePreviewOk['matches']['colours'][number];
export type SupplyPreviewVariantRow = SupplyComposePreviewOk['rows'][number];
export type PreviewSupplyComposeInput = RouterInputs['productSync']['previewSupplyCompose'];
type PreviewInput = PreviewSupplyComposeInput;
type SaveInput = RouterInputs['productSync']['saveSupplyElection'];
type SaveResult = RouterOutputs['productSync']['saveSupplyElection'];
type SaveRefusalResult = Extract<SaveResult, { ok: false }>;
export type SupplySaveCounts = Extract<SaveResult, { ok: true }>['counts'];
/** The saved choice `productSync.getSupplyElection` returns (92-23). */
export type SupplyElection = NonNullable<RouterOutputs['productSync']['getSupplyElection']['election']>;

/**
 * Every refusal the save can show: the server's own (as data), plus
 * `save_uncertain`, a thrown error where the answer never arrived (a timeout,
 * a dropped socket): the save may have gone through, and saving again is safe.
 */
export type SupplySaveRefusalCode = SaveRefusalResult['refusal'] | 'save_uncertain';
export interface SupplySaveRefusal {
  refusal: SupplySaveRefusalCode;
  detail?: { code?: string; sku?: string; locationName?: string };
}

export type SupplyMatchGroup = 'colour' | 'size';

/** The server's key for a store value (`supplyStoreValueKey`): trimmed, lower case, '' for none. */
export function supplyValueKey(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export interface SupplyValueChoice {
  storeValue: string | null;
  /** A code from the blank's own list, or null for "Leave these variants out". */
  blankValue: string | null;
}

/** BLU-1522: some colours (or sizes) of the product on another base garment of the same 3PL. */
export interface SupplyGarmentGroupDraft {
  garment: SharedGarmentOption;
  /** The store colours (or sizes) on this garment, as the product names them. */
  storeValues: string[];
  /** The client's own matches on this garment, keyed like the default garment's. */
  colourMatches: Record<string, SupplyValueChoice>;
  sizeMatches: Record<string, SupplyValueChoice>;
}

export interface SupplyDraft {
  /** The default garment: every colour or size no group takes. */
  garment: SharedGarmentOption | null;
  /** The client's own matches, keyed by `supplyValueKey(storeValue)`. */
  colourMatches: Record<string, SupplyValueChoice>;
  sizeMatches: Record<string, SupplyValueChoice>;
  /** The client's own light or dark choices, keyed by `supplyValueKey(storeColour)`. */
  designChoices: Record<string, { storeColour: string | null; dark: boolean }>;
  /** BLU-1522: which option the groups split the product by; null keeps one base garment. */
  split: SupplyMatchGroup | null;
  groups: SupplyGarmentGroupDraft[];
}

const EMPTY_DRAFT: SupplyDraft = {
  garment: null,
  colourMatches: {},
  sizeMatches: {},
  designChoices: {},
  split: null,
  groups: [],
};

/** The most extra base garments one product takes beside its default (the server's limit). */
export const SUPPLY_MAX_GARMENT_GROUPS = GARMENT_GROUPS_MAX;

/** A blank code's garment: its first segment, upper-cased. */
export function garmentKeyOfCode(code: string): string {
  return (code.trim().split('-')[0] ?? '').toUpperCase();
}

/**
 * BLU-1522: how a saved election splits the product over its garments: per
 * colour when every store colour sits on one garment, else per size when
 * every size does, else per colour by each colour's first variant. Each
 * garment's store values come in product order. Null for one garment.
 */
export function electionGarmentSplit(
  election: Pick<SupplyElection, 'variants'>,
  product: Pick<PreviewProduct, 'variants'>,
): { split: SupplyMatchGroup; valuesByGarment: Map<string, string[]> } | null {
  const byVariant = new Map(election.variants.map((v) => [v.variantId, garmentKeyOfCode(v.blankCode)]));
  const pairs = product.variants
    .map((variant) => ({ variant, garment: byVariant.get(variant.id) }))
    .filter((p): p is { variant: PreviewProduct['variants'][number]; garment: string } => p.garment !== undefined);
  if (new Set(pairs.map((p) => p.garment)).size < 2) return null;
  const splitBy = (dimension: SupplyMatchGroup) => {
    const garmentOf = new Map<string, string>();
    const values = new Map<string, string[]>();
    let consistent = true;
    for (const { variant, garment } of pairs) {
      // A variant with no value of this option says nothing either way.
      const value = (dimension === 'colour' ? variant.colourName : variant.sizeName) ?? null;
      if (!value) continue;
      const key = supplyValueKey(value);
      const seen = garmentOf.get(key);
      if (seen === undefined) {
        garmentOf.set(key, garment);
        values.set(garment, [...(values.get(garment) ?? []), value]);
      } else if (seen !== garment) {
        consistent = false;
      }
    }
    return { consistent, valuesByGarment: values };
  };
  const byColour = splitBy('colour');
  if (byColour.consistent) return { split: 'colour', valuesByGarment: byColour.valuesByGarment };
  const bySize = splitBy('size');
  if (bySize.consistent) return { split: 'size', valuesByGarment: bySize.valuesByGarment };
  return { split: 'colour', valuesByGarment: byColour.valuesByGarment };
}

export const SUPPLY_PREVIEW_DEBOUNCE_MS = 300;

type DesignSetName = PreviewInput['backSets'][number];
const DESIGN_SETS: readonly DesignSetName[] = ['light', 'dark', 'single'];
const SLOT_RE = /(?:^|\|)(light|dark|single)-(front|back)$/;

/**
 * Which print sets carry a back, and whether the single set is staged, from
 * the product's READY staged designs (plain and platen variant slots alike).
 * The same rule the sync plan applies to the run scope (plan.ts: `single`
 * when any single file is staged, `2` when the variant's set has a back).
 */
export function supplyDesignSetsFromStaging(staged: ProductStaging | null | undefined): {
  backSets: DesignSetName[];
  singleSet: boolean;
} {
  const backs = new Set<DesignSetName>();
  let singleSet = false;
  for (const [slot, assignment] of Object.entries(staged ?? {})) {
    if (!readySourceRef(assignment)) continue;
    const match = SLOT_RE.exec(slot);
    if (!match) continue;
    const set = match[1] as DesignSetName;
    if (set === 'single') singleSet = true;
    if (match[2] === 'back') backs.add(set);
  }
  return { backSets: DESIGN_SETS.filter((set) => backs.has(set)), singleSet };
}

/**
 * The ONE place that reads whether a saved election has a synced variant (a
 * sync already wrote at least one elected variant). `getSupplyElection` calls
 * this `synced` today; the 92-23 review may rename it to `locked`. Read
 * through here so a rename is a one-line change. This is NOT the scan
 * summary's flag, which means every elected variant is synced. BLU-1522 S2b:
 * such a choice can no longer be cleared, but it still opens in the composer,
 * where only its synced rows are locked (`supplySyncedLock`).
 */
export function isSupplyElectionLocked(election: SupplyElection): boolean {
  const flags = election as unknown as { locked?: unknown; synced?: unknown };
  return flags.locked === true || flags.synced === true;
}

/**
 * BLU-1522 S2b: what a sync already wrote. A synced variant keeps its blank,
 * its base garment and its light or dark design, and is never left out (the
 * save refuses `already_synced` otherwise), so every row that carries one is
 * locked. Keys are `supplyValueKey` store values; garments are upper-case
 * keys.
 */
export interface SupplySyncedLock {
  /** Store colours with a synced variant on any base garment. */
  colours: ReadonlySet<string>;
  /** Store sizes with a synced variant on any base garment. */
  sizes: ReadonlySet<string>;
  /** Per base garment: the store colours and sizes of its synced variants. */
  byGarment: ReadonlyMap<string, { colours: ReadonlySet<string>; sizes: ReadonlySet<string> }>;
}

/**
 * The lock of a saved election, from its own data only (no read): its
 * `syncedVariantIds` (an older answer without the list but marked synced
 * locks every elected variant, failing closed), each variant's saved blank
 * code for its base garment, and the product's own colour and size names.
 * Null when nothing is synced. Only the product's current variants count: a
 * synced variant deleted from the store keeps its election on the server and
 * never blocks a save on the same 3PL, so it locks nothing here.
 */
export function supplySyncedLock(
  election: SupplyElection,
  product: Pick<PreviewProduct, 'variants'>,
): SupplySyncedLock | null {
  const listed = (election as unknown as { syncedVariantIds?: unknown }).syncedVariantIds;
  const ids = new Set<string>(
    Array.isArray(listed)
      ? listed.filter((id): id is string => typeof id === 'string')
      : isSupplyElectionLocked(election)
        ? election.variants.map((v) => v.variantId)
        : [],
  );
  if (ids.size === 0) return null;
  const codeOf = new Map(election.variants.map((v) => [v.variantId, v.blankCode]));
  const colours = new Set<string>();
  const sizes = new Set<string>();
  const byGarment = new Map<string, { colours: Set<string>; sizes: Set<string> }>();
  for (const variant of product.variants) {
    const code = codeOf.get(variant.id);
    if (!ids.has(variant.id) || code === undefined) continue;
    const colour = supplyValueKey(variant.colourName);
    const size = supplyValueKey(variant.sizeName);
    const garment = garmentKeyOfCode(code);
    let part = byGarment.get(garment);
    if (!part) {
      part = { colours: new Set(), sizes: new Set() };
      byGarment.set(garment, part);
    }
    colours.add(colour);
    sizes.add(size);
    part.colours.add(colour);
    part.sizes.add(size);
  }
  return byGarment.size > 0 ? { colours, sizes, byGarment } : null;
}

/**
 * Whether a match row carries a synced variant (BLU-1522 S2b). One base
 * garment, or the option the product is split by: the store value has a
 * synced variant anywhere. The other option of a split product: on that row's
 * garment (`garmentKey`, else the default garment).
 */
export function isSupplyRowLocked(
  lock: SupplySyncedLock | null,
  draft: Pick<SupplyDraft, 'split' | 'garment'>,
  group: SupplyMatchGroup,
  storeValue: string | null,
  garmentKey?: string,
): boolean {
  if (!lock) return false;
  const key = supplyValueKey(storeValue);
  const field = group === 'colour' ? 'colours' : 'sizes';
  if (!draft.split || group === draft.split) return lock[field].has(key);
  const garment = (garmentKey ?? draft.garment?.garmentKey ?? '').toUpperCase();
  return lock.byGarment.get(garment)?.[field].has(key) ?? false;
}

/**
 * BLU-1522 S2b: a new default garment starts the draft over, which would move
 * every synced variant, so it is locked while any variant is synced.
 */
export function isSupplyGarmentLocked(lock: SupplySyncedLock | null): boolean {
  return lock !== null && lock.byGarment.size > 0;
}

/**
 * BLU-1522 S2b: changing or closing the split clears the groups, which moves
 * their variants back to the default garment, so it is locked while a synced
 * variant sits on a base garment other than the default.
 */
export function isSupplySplitLocked(lock: SupplySyncedLock | null, draft: Pick<SupplyDraft, 'garment'>): boolean {
  if (!lock) return false;
  const main = draft.garment?.garmentKey.toUpperCase() ?? '';
  return [...lock.byGarment.keys()].some((key) => key !== main);
}

/**
 * A saved election as a draft, for "Change blank" (92-27b): the garment, one
 * match per store value that has an elected variant (the blank's own colour
 * and size segments of that variant's blank code), and the light or dark
 * choice per store colour. A store value with no elected variant (left out or
 * held back when saved) gets no entry, so the server auto-matches it again or
 * asks the client to choose. The garment's counts are the blank colours and
 * sizes this product uses (the election does not carry the garment's totals).
 * Built only from the client's own saved snapshot (codes, light or dark) plus
 * the garment keys, so it works even when the 3PL's live values are withheld
 * (a revoked connection, or a caller the server gives the snapshot only).
 * BLU-1522: an election on several garments opens with the garment most
 * elected variants use as the default (a tie in product order, as the scan
 * summary decides it) and one group per other garment (`electionGarmentSplit`).
 */
export function draftFromElection(election: SupplyElection, product: Pick<PreviewProduct, 'variants'>): SupplyDraft {
  const live = election as unknown as {
    garmentTitle?: string | null;
    garmentImageUrl?: string | null;
    garments?: Array<{ garmentKey: string; title: string; imageUrl: string | null }>;
  };
  const byVariant = new Map(election.variants.map((v) => [v.variantId, v]));
  const designChoices: SupplyDraft['designChoices'] = {};
  const parts = new Map<
    string,
    {
      colourMatches: Record<string, SupplyValueChoice>;
      sizeMatches: Record<string, SupplyValueChoice>;
      colours: Set<string>;
      sizes: Set<string>;
      blanks: Set<string>;
    }
  >();
  const uses = new Map<string, number>();
  for (const variant of product.variants) {
    const elected = byVariant.get(variant.id);
    if (!elected) continue;
    const key = garmentKeyOfCode(elected.blankCode);
    uses.set(key, (uses.get(key) ?? 0) + 1);
  }
  const serverDefaultKey = election.garmentKey.toUpperCase();
  // The Map keeps product order and the sort is stable, so a tie goes to the first.
  const defaultKey = [...uses.keys()].sort((a, b) => uses.get(b)! - uses.get(a)!)[0] ?? serverDefaultKey;
  const partOf = (key: string) => {
    let part = parts.get(key);
    if (!part) {
      part = { colourMatches: {}, sizeMatches: {}, colours: new Set(), sizes: new Set(), blanks: new Set() };
      parts.set(key, part);
    }
    return part;
  };
  partOf(defaultKey);

  for (const variant of product.variants) {
    const elected = byVariant.get(variant.id);
    if (!elected) continue;
    const segments = elected.blankCode.trim().toUpperCase().split('-');
    if (segments.length !== 2 && segments.length !== 3) continue;
    const part = partOf(segments[0]!);
    const colourSegment = segments[segments.length - 1]!;
    const sizeSegment = segments.length === 3 ? segments[1]! : null;
    const storeColour = variant.colourName ?? null;
    const storeSize = variant.sizeName ?? null;
    const colourKey = supplyValueKey(storeColour);
    part.colourMatches[colourKey] ??= { storeValue: storeColour, blankValue: colourSegment };
    designChoices[colourKey] ??= { storeColour, dark: elected.isDark === true };
    if (sizeSegment) {
      part.sizeMatches[supplyValueKey(storeSize)] ??= { storeValue: storeSize, blankValue: sizeSegment };
      part.sizes.add(sizeSegment);
    }
    part.colours.add(colourSegment);
    part.blanks.add(elected.blankSkuId ?? elected.blankCode);
  }

  const optionOf = (key: string): SharedGarmentOption => {
    const part = partOf(key);
    const named = live.garments?.find((g) => g.garmentKey.toUpperCase() === key);
    // The flat title and image belong to the server's default garment.
    const serverDefault = key === serverDefaultKey;
    return {
      garmentKey: key,
      title: named?.title?.trim() || (serverDefault ? live.garmentTitle?.trim() : '') || key,
      imageUrl: named?.imageUrl ?? (serverDefault ? (live.garmentImageUrl ?? null) : null),
      colourCount: part.colours.size,
      sizeCount: part.sizes.size,
      blankCount: part.blanks.size,
      usedByCaller: true,
    };
  };
  const main = partOf(defaultKey);
  const split = electionGarmentSplit(election, product);
  const groups: SupplyGarmentGroupDraft[] = [];
  if (split) {
    for (const [key, storeValues] of split.valuesByGarment) {
      if (key === defaultKey) continue;
      const part = partOf(key);
      groups.push({ garment: optionOf(key), storeValues, colourMatches: part.colourMatches, sizeMatches: part.sizeMatches });
    }
  }

  return {
    garment: optionOf(defaultKey),
    colourMatches: main.colourMatches,
    sizeMatches: main.sizeMatches,
    designChoices,
    split: split && groups.length > 0 ? split.split : null,
    groups: groups.slice(0, SUPPLY_MAX_GARMENT_GROUPS),
  };
}

const KNOWN_REFUSALS: ReadonlySet<string> = new Set<SupplySaveRefusalCode>([
  'connection_inactive',
  'consent_off',
  'blank_not_offered',
  'split_unavailable',
  'code_clash',
  'print_sku_in_use',
  'intake_off',
  'already_synced',
  'sync_running',
  'store_sku_mode',
  'nothing_ready',
  'mode_conflict',
  'save_failed',
]);

function knownRefusal(raw: string | undefined): SaveRefusalResult['refusal'] | null {
  if (!raw) return null;
  const code = raw.trim().toLowerCase().replace(/^supply_/, '');
  return KNOWN_REFUSALS.has(code) ? (code as SaveRefusalResult['refusal']) : null;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : '';
}

/** `PRINT_SKU_IN_USE:<sku>`, `SUPPLY_CODE_CLASH:<code>`, or a bare code, anchored to the whole message. */
const REFUSAL_MARKER = /^(?:SUPPLY_)?([A-Z_]+?)(?::(.*))?$/;

function detailFor(refusal: SaveRefusalResult['refusal'], value: string | null | undefined): SupplySaveRefusal['detail'] {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return undefined;
  if (refusal === 'print_sku_in_use') return { sku: text };
  if (refusal === 'code_clash') return { code: text };
  return undefined;
}

/**
 * A thrown save error as a refusal: the forwarded cause code first, the
 * message marker second (causes are dropped on the wire until BLU-1277), and
 * the generic refusal last. An answer that never arrived is `save_uncertain`.
 */
export function supplyRefusalFromError(error: unknown): SupplySaveRefusal {
  const fromCause = knownRefusal(readCauseCode(error));
  if (fromCause) {
    const cause = ((error as { data?: { cause?: unknown } } | null)?.data?.cause ??
      (error as { cause?: unknown } | null)?.cause) as
      | { sku?: unknown; code?: unknown; locationName?: unknown; detail?: unknown }
      | undefined;
    const detail: SupplySaveRefusal['detail'] = {};
    if (typeof cause?.sku === 'string') detail.sku = cause.sku;
    if (typeof cause?.locationName === 'string') detail.locationName = cause.locationName;
    if (typeof cause?.detail === 'string') Object.assign(detail, detailFor(fromCause, cause.detail));
    return Object.keys(detail).length > 0 ? { refusal: fromCause, detail } : { refusal: fromCause };
  }
  const marker = REFUSAL_MARKER.exec(errorMessage(error).trim());
  const fromMarker = marker ? knownRefusal(marker[1]) : null;
  if (fromMarker) {
    const detail = detailFor(fromMarker, marker?.[2]);
    return detail ? { refusal: fromMarker, detail } : { refusal: fromMarker };
  }
  if (isUncertainOutcomeError(error)) return { refusal: 'save_uncertain' };
  return { refusal: 'save_failed' };
}

/**
 * The plain words for a save refusal (UI-SPEC Screen 4, 92-27 refusal table).
 * Names come from the API; a `code_clash` the SQL answered has no code, and a
 * refusal without its detail falls back to the last preview, then to a line
 * that needs neither.
 */
export function supplyRefusalMessage(
  t: TFunction<'productSync'>,
  refusal: SupplySaveRefusal,
  context: { ownerName: string; preview: SupplyComposePreviewOk | null; split?: SupplyMatchGroup | null },
): string {
  const { ownerName, preview } = context;
  switch (refusal.refusal) {
    case 'split_unavailable':
      return context.split === 'size'
        ? t('supply.refusal.splitUnavailableSize')
        : context.split === 'colour'
          ? t('supply.refusal.splitUnavailableColour')
          : t('supply.refusal.splitUnavailable');
    case 'print_sku_in_use': {
      const row = preview?.rows.find((r) => r.status === 'in_use');
      const sku = refusal.detail?.sku ?? row?.printSku ?? null;
      const location =
        refusal.detail?.locationName ??
        (row?.locationId ? preview?.locations.find((l) => l.id === row.locationId)?.name : undefined) ??
        null;
      if (sku && location) return t('supply.refusal.printSkuInUse', { sku, location });
      if (sku) return t('supply.refusal.printSkuInUseSomewhere', { sku });
      return t('supply.refusal.printSkuInUseUnknown');
    }
    case 'code_clash': {
      const code = refusal.detail?.code ?? preview?.codeClash?.code ?? null;
      return code
        ? t('supply.refusal.codeClash', { code, owner: ownerName })
        : t('supply.refusal.codeClashNoCode', { owner: ownerName });
    }
    case 'consent_off':
      return t('supply.refusal.consentOff', { owner: ownerName });
    case 'blank_not_offered':
      return t('supply.refusal.blankNotOffered', { owner: ownerName });
    case 'connection_inactive':
      return t('sharedStock.reason.connection_inactive');
    case 'intake_off':
      return t('job.blocker.supply_intake_off');
    case 'already_synced':
      return t('supply.refusal.alreadySynced');
    case 'sync_running':
      return t('supply.refusal.syncRunning');
    case 'store_sku_mode':
      return t('job.error.org_uses_store_skus');
    case 'nothing_ready':
      return t('supply.gate.nothingReady');
    case 'mode_conflict':
      return t('supply.refusal.modeConflict');
    case 'save_uncertain':
      return t('supply.refusal.saveUncertain');
    default:
      return t('supply.refusal.saveFailed');
  }
}

export interface UseSupplyComposerOptions {
  /** Which print sets carry a back (`supplyDesignSetsFromStaging`). */
  backSets?: PreviewInput['backSets'];
  singleSet?: boolean;
}

/** What assigning a garment to some store values did. */
export type SupplyAssignOutcome = 'assigned' | 'too_many';

export interface UseSupplyComposerResult {
  draft: SupplyDraft;
  setGarment: (garment: SharedGarmentOption) => void;
  /**
   * A match on the default garment, or (BLU-1522) on the group garment whose
   * key is given.
   */
  setMatch: (group: SupplyMatchGroup, storeValue: string | null, blankValue: string | null, garmentKey?: string) => void;
  /** BLU-1522: split the product per colour or per size (clears the groups), or null for one base garment. */
  setSplit: (split: SupplyMatchGroup | null) => void;
  /**
   * BLU-1522: put these store values on a garment. The default garment takes
   * them back; another garment joins its group or starts one (refused past
   * `SUPPLY_MAX_GARMENT_GROUPS`).
   */
  assignGarment: (storeValues: string[], garment: SharedGarmentOption) => SupplyAssignOutcome;
  setDesignChoice: (storeColour: string | null, dark: boolean) => void;
  /** Light or dark for a store colour: the client's choice, else the 3PL's suggestion, else light. */
  designFor: (storeColour: string | null) => boolean;
  discard: () => void;
  /**
   * Seed the draft from a saved election ("Change blank", 92-27b). BLU-1522
   * S2b: a choice with synced variants opens too, with `lock` set from it.
   */
  openWith: (election: SupplyElection) => boolean;
  /** BLU-1522 S2b: the synced rows of the opened election; null when nothing is synced. */
  lock: SupplySyncedLock | null;
  /** BLU-1522 S2b: a match row that carries a synced variant (no picker, no tick, no light or dark change). */
  isRowLocked: (group: SupplyMatchGroup, storeValue: string | null, garmentKey?: string) => boolean;
  /** BLU-1522 S2b: the default garment cannot change (a synced variant would move). */
  garmentLocked: boolean;
  /** BLU-1522 S2b: the split cannot change or close (a synced variant sits on another base garment). */
  splitLocked: boolean;
  /** The last successful preview (a refusal is a successful answer too). */
  preview: SupplyComposePreview | null;
  previewLoading: boolean;
  previewError: boolean;
  retryPreview: () => void;
  /** Save the current draft once; a call while one is in flight is ignored. */
  save: () => Promise<void>;
  saving: boolean;
  /** The save of THIS draft succeeded (cleared when the draft changes). */
  saved: { changed: boolean; counts: SupplySaveCounts } | null;
  /** The save of THIS draft was refused (cleared when the draft changes). */
  refusal: SupplySaveRefusal | null;
  /** `refusal` in plain words, in the current language. */
  refusalMessage: string | null;
}

function sortedChoices(record: Record<string, SupplyValueChoice>): SupplyValueChoice[] {
  return Object.keys(record)
    .sort()
    .map((key) => record[key]!);
}

type SaveOutcome =
  | { key: string; kind: 'saved'; changed: boolean; counts: SupplySaveCounts }
  | { key: string; kind: 'refused'; refusal: SupplySaveRefusal };

export function useSupplyComposer(
  product: PreviewProduct,
  owner: { id: string; name?: string } | null,
  options: UseSupplyComposerOptions = {},
): UseSupplyComposerResult {
  const { t } = useTranslation('productSync');
  const [draft, setDraft] = useState<SupplyDraft>(EMPTY_DRAFT);
  const [preview, setPreview] = useState<SupplyComposePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  // BLU-1522 S2b: the opened election's synced rows (a property of the
  // product, so discard keeps it; the composer closes on discard anyway).
  const [lock, setLock] = useState<SupplySyncedLock | null>(null);
  const lockRef = useRef(lock);
  lockRef.current = lock;

  const utils = trpc.useUtils();
  const mutation = trpc.productSync.previewSupplyCompose.useMutation();
  const mutateRef = useRef(mutation.mutateAsync);
  mutateRef.current = mutation.mutateAsync;
  const saveMutation = trpc.productSync.saveSupplyElection.useMutation();
  const saveRef = useRef(saveMutation.mutateAsync);
  saveRef.current = saveMutation.mutateAsync;
  const latestRequest = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  // A save in flight; read synchronously so a second Enter can never send twice.
  const savingRef = useRef(false);
  // Bumped by discard and openWith, so a save answering after either is dropped.
  const generation = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // The 3PL's per-colour suggestion from the last result, keyed by store
  // colour, on whichever garment the colour is.
  const suggestedRows = useMemo(() => {
    if (!preview?.ok) return [];
    return [...preview.matches.colours, ...(preview.groups ?? []).flatMap((g) => g.matches.colours)];
  }, [preview]);
  const suggested = useMemo(() => {
    const out = new Map<string, boolean>();
    for (const row of suggestedRows) out.set(supplyValueKey(row.storeValue), row.suggestedDark);
    return out;
  }, [suggestedRows]);

  const designFor = useCallback(
    (storeColour: string | null) => {
      const key = supplyValueKey(storeColour);
      return draft.designChoices[key]?.dark ?? suggested.get(key) ?? false;
    },
    [draft.designChoices, suggested],
  );

  const backSets = options.backSets;
  const singleSet = options.singleSet ?? false;
  const backSetsKey = (backSets ?? []).join(',');

  // The client's choices: everything the save sends (the preview adds the variants).
  const choices = useMemo<SaveInput | null>(() => {
    if (!owner || !draft.garment || product.variants.length === 0) return null;
    // Explicit choices win; otherwise the suggestion pre-fills dark. Light
    // with no explicit choice is the server default and is not sent.
    const design = new Map<string, { storeColour: string | null; dark: boolean }>();
    for (const row of suggestedRows) {
      if (row.suggestedDark) design.set(supplyValueKey(row.storeValue), { storeColour: row.storeValue, dark: true });
    }
    for (const [key, choice] of Object.entries(draft.designChoices)) design.set(key, choice);
    // Groups are sent only once one exists, so the default view sends what it always has.
    const split = draft.groups.length > 0 ? draft.split : null;
    return {
      ownerOrganizationId: owner.id,
      garmentKey: draft.garment.garmentKey,
      productId: product.id,
      designCode: product.designCode,
      colourMatches: sortedChoices(draft.colourMatches),
      sizeMatches: sortedChoices(draft.sizeMatches),
      designChoices: [...design.keys()].sort().map((key) => design.get(key)!),
      backSets: backSetsKey ? (backSetsKey.split(',') as PreviewInput['backSets']) : [],
      singleSet,
      ...(split
        ? {
            split,
            groups: draft.groups.map((group) => ({
              garmentKey: group.garment.garmentKey,
              storeValues: [...group.storeValues],
              colourMatches: sortedChoices(group.colourMatches),
              sizeMatches: sortedChoices(group.sizeMatches),
            })),
          }
        : {}),
    };
  }, [owner, draft, suggestedRows, product.id, product.designCode, product.variants.length, backSetsKey, singleSet]);

  const input = useMemo<PreviewInput | null>(() => {
    if (!choices) return null;
    return {
      ...choices,
      productTypeName: product.productType.name ?? '',
      variants: product.variants.map((v) => ({
        variantId: v.id,
        colourName: v.colourName ?? null,
        sizeName: v.sizeName ?? null,
      })),
    };
  }, [choices, product.productType.name, product.variants]);

  const inputKey = input ? JSON.stringify(input) : null;
  const inputRef = useRef(input);
  inputRef.current = input;
  const choicesKey = choices ? JSON.stringify(choices) : null;
  const choicesRef = useRef(choices);
  choicesRef.current = choices;

  const send = useCallback((request: PreviewInput) => {
    const id = ++latestRequest.current;
    setPreviewLoading(true);
    mutateRef.current(request).then(
      (result) => {
        if (!mounted.current || id !== latestRequest.current) return;
        setPreview(result);
        setPreviewError(false);
        setPreviewLoading(false);
      },
      () => {
        if (!mounted.current || id !== latestRequest.current) return;
        // One error state for every failure, TOO_MANY_REQUESTS included (P9).
        setPreviewError(true);
        setPreviewLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    if (!inputKey) return;
    setPreviewLoading(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      if (inputRef.current) send(inputRef.current);
    }, SUPPLY_PREVIEW_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [inputKey, send]);

  const retryPreview = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (inputRef.current) send(inputRef.current);
  }, [send]);

  const toastId = `supply-save-${product.id}`;
  const ownerName = owner?.name ?? '';
  const previewOk = preview?.ok ? preview : null;
  const previewOkRef = useRef(previewOk);
  previewOkRef.current = previewOk;

  /** Forget the result and anything in flight (a new garment, discard, or a saved election). */
  const reset = useCallback(() => {
    latestRequest.current += 1;
    generation.current += 1;
    setPreview(null);
    setPreviewError(false);
    setPreviewLoading(false);
    setOutcome(null);
  }, []);

  const save = useCallback(async () => {
    const request = choicesRef.current;
    if (savingRef.current || !request) return;
    savingRef.current = true;
    setSaving(true);
    const key = JSON.stringify(request);
    const gen = generation.current;
    const stale = () => !mounted.current || gen !== generation.current;
    const refuse = (refusal: SupplySaveRefusal) => {
      setOutcome({ key, kind: 'refused', refusal });
      toast.error(supplyRefusalMessage(t, refusal, { ownerName, preview: previewOkRef.current }), {
        id: toastId,
        duration: Infinity,
      });
    };
    try {
      const result = await saveRef.current(request);
      if (stale()) return;
      if (result.ok) {
        // No toast on success (DESIGN.md §15.4): the footer becomes the saved note.
        toast.dismiss(toastId);
        setOutcome({ key, kind: 'saved', changed: result.changed, counts: result.counts });
        void utils.productSync.getProductSyncPreview.invalidate();
        void utils.productSync.getSupplyElection.invalidate({ productId: request.productId });
      } else {
        refuse(result.detail ? { refusal: result.refusal, detail: result.detail } : { refusal: result.refusal });
      }
    } catch (error) {
      if (stale()) return;
      refuse(supplyRefusalFromError(error));
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [t, ownerName, toastId, utils]);

  const setGarment = useCallback(
    (garment: SharedGarmentOption) => {
      // BLU-1522 S2b: starting over would move a synced variant.
      if (isSupplyGarmentLocked(lockRef.current)) return;
      reset();
      // A new default garment starts over; an open per colour / per size switch stays open.
      setDraft((current) => ({ ...EMPTY_DRAFT, garment, split: current.split }));
    },
    [reset],
  );

  const setMatch = useCallback(
    (group: SupplyMatchGroup, storeValue: string | null, blankValue: string | null, garmentKey?: string) => {
      setDraft((current) => {
        // BLU-1522 S2b: a row with a synced variant keeps its match.
        if (isSupplyRowLocked(lockRef.current, current, group, storeValue, garmentKey)) return current;
        const field = group === 'colour' ? 'colourMatches' : 'sizeMatches';
        const choice = { storeValue, blankValue };
        const key = garmentKey?.toUpperCase();
        const at = key ? current.groups.findIndex((g) => g.garment.garmentKey.toUpperCase() === key) : -1;
        if (at < 0) return { ...current, [field]: { ...current[field], [supplyValueKey(storeValue)]: choice } };
        const groups = current.groups.slice();
        groups[at] = { ...groups[at]!, [field]: { ...groups[at]![field], [supplyValueKey(storeValue)]: choice } };
        return { ...current, groups };
      });
    },
    [],
  );

  const setSplit = useCallback((split: SupplyMatchGroup | null) => {
    setDraft((current) => {
      if (current.split === split) return current;
      // BLU-1522 S2b: clearing the groups would move a synced variant to the default garment.
      if (current.groups.length > 0 && isSupplySplitLocked(lockRef.current, current)) return current;
      return { ...current, split, groups: [] };
    });
  }, []);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const assignGarment = useCallback((picked: string[], garment: SharedGarmentOption): SupplyAssignOutcome => {
    const current = draftRef.current;
    const split = current.split;
    if (!split) return 'assigned';
    // BLU-1522 S2b: a value with a synced variant never moves to another garment.
    const storeValues = picked.filter((value) => !isSupplyRowLocked(lockRef.current, current, split, value));
    if (storeValues.length === 0) return 'assigned';
    const field = split === 'colour' ? 'colourMatches' : 'sizeMatches';
    const moving = new Set(storeValues.map(supplyValueKey));
    const without = (matches: Record<string, SupplyValueChoice>) =>
      Object.fromEntries(Object.entries(matches).filter(([key]) => !moving.has(key)));
    const key = garment.garmentKey.toUpperCase();
    // The shared move (lib/garment-groups): the default garment takes values back with no group.
    const moved = moveValuesToGarment(
      current.groups,
      storeValues,
      {
        key: key === current.garment?.garmentKey.toUpperCase() ? null : key,
        create: (values): SupplyGarmentGroupDraft => ({ garment, storeValues: values, colourMatches: {}, sizeMatches: {} }),
      },
      {
        keyOf: (group) => group.garment.garmentKey.toUpperCase(),
        strip: (group): SupplyGarmentGroupDraft => ({ ...group, [field]: without(group[field]) }),
        max: SUPPLY_MAX_GARMENT_GROUPS,
      },
    );
    if (moved.outcome === 'too_many') return 'too_many';
    const next: SupplyDraft = { ...current, [field]: without(current[field]), groups: moved.groups };
    draftRef.current = next;
    setDraft(next);
    return 'assigned';
  }, []);

  const setDesignChoice = useCallback((storeColour: string | null, dark: boolean) => {
    // BLU-1522 S2b: a colour with a synced variant keeps its light or dark design.
    if (lockRef.current?.colours.has(supplyValueKey(storeColour))) return;
    setDraft((current) => ({
      ...current,
      designChoices: { ...current.designChoices, [supplyValueKey(storeColour)]: { storeColour, dark } },
    }));
  }, []);

  const discard = useCallback(() => {
    reset();
    toast.dismiss(toastId);
    setDraft(EMPTY_DRAFT);
  }, [reset, toastId]);

  const productRef = useRef(product);
  productRef.current = product;
  const openWith = useCallback(
    (election: SupplyElection) => {
      // BLU-1522 S2b: a choice with synced variants opens too; only its synced rows lock.
      reset();
      const next = supplySyncedLock(election, productRef.current);
      lockRef.current = next;
      setLock(next);
      setDraft(draftFromElection(election, productRef.current));
      return true;
    },
    [reset],
  );

  const isRowLocked = useCallback(
    (group: SupplyMatchGroup, storeValue: string | null, garmentKey?: string) =>
      isSupplyRowLocked(lock, draft, group, storeValue, garmentKey),
    [lock, draft],
  );

  // An outcome belongs to the draft it was saved from; any change clears it.
  const current = outcome && outcome.key === choicesKey ? outcome : null;
  const saved = current?.kind === 'saved' ? { changed: current.changed, counts: current.counts } : null;
  const refusal = current?.kind === 'refused' ? current.refusal : null;
  const refusalMessage = refusal
    ? supplyRefusalMessage(t, refusal, { ownerName, preview: previewOk, split: draft.split })
    : null;

  return {
    draft,
    setGarment,
    setMatch,
    setSplit,
    assignGarment,
    setDesignChoice,
    designFor,
    discard,
    openWith,
    lock,
    isRowLocked,
    garmentLocked: isSupplyGarmentLocked(lock),
    splitLocked: draft.groups.length > 0 && isSupplySplitLocked(lock, draft),
    preview,
    previewLoading,
    previewError,
    retryPreview,
    save,
    saving,
    saved,
    refusal,
    refusalMessage,
  };
}
